import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Mail, Globe2, MapPin, CheckCircle2 } from "lucide-react";
import { BRAND } from "@/lib/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "B2B E-commerce Platform",
  "API & Integrations",
  "ERP / CRM Connection",
  "Brokerage & Intermediation",
  "Payments & Logistics",
  "Custom Web / Mobile",
  "Not sure yet",
];

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  country: "",
  service_interest: "",
  message: "",
  website: "",
};

const validate = (v) => {
  const e = {};
  if (!v.name || v.name.trim().length < 2) e.name = "Please enter your full name.";
  if (!v.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = "Enter a valid work email.";
  if (!v.company || v.company.trim().length < 1) e.company = "Company is required.";
  if (!v.message || v.message.trim().length < 10) e.message = "Tell us a bit more (10+ characters).";
  return e;
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length > 0) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form, { timeout: 15000 });
      if (res.status >= 200 && res.status < 300) {
        setSubmitted(true);
        setForm(initial);
        toast.success("Message sent. Our team will be in touch shortly.", {
          id: "contact-form-success-toast",
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-pad relative border-t border-[rgba(244,234,222,0.08)]">
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(700px circle at 80% 10%, rgba(107,31,71,0.18), transparent 60%)",
          }}
        />
      </div>
      <div className="relative container-narrow">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.45 }}
            className="lg:col-span-5"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#E94B4B]">
              — Contact
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F4EADE]">
              Start a <span className="text-coral">conversation.</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#EFE3D6]/80 leading-relaxed">
              Share the shape of the project or the partnership. We respond within one
              business day with a clear perspective and next steps.
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3 text-[#EFE3D6]">
                <div className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-[#6B1F47]/25 ring-1 ring-[#6B1F47]/40">
                  <Mail className="h-4 w-4 text-[#F4EADE]" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">Email</div>
                  <div className="font-display text-sm">{BRAND.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[#EFE3D6]">
                <div className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-[#6B1F47]/25 ring-1 ring-[#6B1F47]/40">
                  <Globe2 className="h-4 w-4 text-[#F4EADE]" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">Coverage</div>
                  <div className="font-display text-sm">Worldwide — EMEA, AMER, APAC</div>
                </div>
              </div>
              <div className="flex items-start gap-3 text-[#EFE3D6]">
                <div className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-[#6B1F47]/25 ring-1 ring-[#6B1F47]/40">
                  <MapPin className="h-4 w-4 text-[#F4EADE]" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">Headquarters</div>
                  <div className="font-display text-sm">{BRAND.address}</div>
                  <div className="font-display text-sm text-[#EFE3D6]/80">{BRAND.addressLine2}</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="lg:col-span-7"
          >
            <div className="rounded-2xl border border-[rgba(244,234,222,0.14)] bg-[rgba(17,16,21,0.8)] p-6 sm:p-8">
              {submitted ? (
                <div
                  data-testid="contact-form-success-state"
                  className="flex flex-col items-start gap-4 py-6"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#2FBF71]/15 ring-1 ring-[#2FBF71]/40 text-[#2FBF71]">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-2xl text-[#F4EADE]">Message received.</h3>
                  <p className="text-sm text-[#EFE3D6]/80">
                    Thanks for reaching out. Our team will respond within one business day.
                  </p>
                  <Button
                    type="button"
                    data-testid="contact-form-send-another-button"
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="bg-transparent border-[rgba(244,234,222,0.22)] text-[#F4EADE] hover:bg-[rgba(244,234,222,0.06)]"
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" noValidate>
                  <div className="hidden" aria-hidden="true">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={onChange("website")}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#EFE3D6]">Full name <span className="text-coral">*</span></Label>
                      <Input
                        id="name"
                        data-testid="contact-form-name-input"
                        value={form.name}
                        onChange={onChange("name")}
                        placeholder="Jane Doe"
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? "name-error" : undefined}
                        className="bg-[#0B0A0C] border-[rgba(244,234,222,0.18)] text-[#F4EADE] placeholder:text-[#B9AFA6]/60 focus-visible:ring-[#E94B4B]"
                      />
                      {errors.name && (
                        <p id="name-error" data-testid="contact-form-error-text" className="text-xs text-[#E94B4B]">
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#EFE3D6]">Work email <span className="text-coral">*</span></Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-form-email-input"
                        value={form.email}
                        onChange={onChange("email")}
                        placeholder="jane@company.com"
                        aria-invalid={!!errors.email}
                        className="bg-[#0B0A0C] border-[rgba(244,234,222,0.18)] text-[#F4EADE] placeholder:text-[#B9AFA6]/60 focus-visible:ring-[#E94B4B]"
                      />
                      {errors.email && (
                        <p data-testid="contact-form-error-text" className="text-xs text-[#E94B4B]">
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-[#EFE3D6]">Company <span className="text-coral">*</span></Label>
                      <Input
                        id="company"
                        data-testid="contact-form-company-input"
                        value={form.company}
                        onChange={onChange("company")}
                        placeholder="Acme Global Trade"
                        aria-invalid={!!errors.company}
                        className="bg-[#0B0A0C] border-[rgba(244,234,222,0.18)] text-[#F4EADE] placeholder:text-[#B9AFA6]/60 focus-visible:ring-[#E94B4B]"
                      />
                      {errors.company && (
                        <p data-testid="contact-form-error-text" className="text-xs text-[#E94B4B]">
                          {errors.company}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-[#EFE3D6]">Country / Region</Label>
                      <Input
                        id="country"
                        data-testid="contact-form-country-input"
                        value={form.country}
                        onChange={onChange("country")}
                        placeholder="e.g. Germany"
                        className="bg-[#0B0A0C] border-[rgba(244,234,222,0.18)] text-[#F4EADE] placeholder:text-[#B9AFA6]/60 focus-visible:ring-[#E94B4B]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#EFE3D6]">Phone</Label>
                      <Input
                        id="phone"
                        data-testid="contact-form-phone-input"
                        value={form.phone}
                        onChange={onChange("phone")}
                        placeholder="+1 555 123 4567"
                        className="bg-[#0B0A0C] border-[rgba(244,234,222,0.18)] text-[#F4EADE] placeholder:text-[#B9AFA6]/60 focus-visible:ring-[#E94B4B]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service" className="text-[#EFE3D6]">Interest</Label>
                      <Select
                        value={form.service_interest}
                        onValueChange={(v) => setForm((f) => ({ ...f, service_interest: v }))}
                      >
                        <SelectTrigger
                          id="service"
                          data-testid="contact-form-service-select"
                          className="bg-[#0B0A0C] border-[rgba(244,234,222,0.18)] text-[#F4EADE] focus:ring-[#E94B4B]"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111015] border-[rgba(244,234,222,0.16)] text-[#F4EADE]">
                          {SERVICE_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={opt} data-testid={`service-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <Label htmlFor="message" className="text-[#EFE3D6]">Project summary <span className="text-coral">*</span></Label>
                    <Textarea
                      id="message"
                      data-testid="contact-form-message-textarea"
                      value={form.message}
                      onChange={onChange("message")}
                      placeholder="Tell us about your platform, integrations, partners, timelines and what success looks like."
                      rows={5}
                      aria-invalid={!!errors.message}
                      className="bg-[#0B0A0C] border-[rgba(244,234,222,0.18)] text-[#F4EADE] placeholder:text-[#B9AFA6]/60 focus-visible:ring-[#E94B4B]"
                    />
                    {errors.message && (
                      <p data-testid="contact-form-error-text" className="text-xs text-[#E94B4B]">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <p className="text-xs text-[#B9AFA6]">
                      By submitting, you agree to be contacted by GREINCH WW LLC regarding your inquiry.
                    </p>
                    <Button
                      type="submit"
                      data-testid="contact-form-submit-button"
                      disabled={loading}
                      className="h-12 px-6 bg-[#6B1F47] hover:bg-[#7A1F4A] text-[#F4EADE] btn-lift focus-ring disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? "Sending…" : (<><Send className="mr-2 h-4 w-4" /> Send message</>)}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
